<template>
  <div class="ai-panel">
    <!-- 顶部标题 -->
    <div class="ai-header">
      <div class="ai-title">AI 文书助手</div>
      <!-- <a-button type="text" @click="clearChat">清空</a-button> -->
    </div>

    <!-- 消息区域 -->
    <div ref="messageBox" class="message-container">
      <div
        v-for="item in messages"
        :key="item.id"
        class="message-item"
        :class="item.role"
      >
        <!-- AI消息：头像在左，内容在右 -->
        <template v-if="item.role === 'assistant'">
          <div class="avatar ai-avatar"><span class="avatar-text">AI</span></div>
          <div class="message-content">
            <!-- 普通文本气泡 -->
            <div v-if="item.type === 'text'" class="bubble ai-bubble" v-html="item.content "></div>

            <!-- 文件卡片 -->
            <div v-if="item.type === 'file'" class="file-card">
              <div class="file-w-icon">W</div>
              <div class="file-text-wrap">
                <div class="file-name">{{ item.fileName }}</div>
                <div class="file-size">{{ item.fileSize }}</div>
              </div>
              <span class="file-done">已完成</span>
              <div class="download-box"></div>
            </div>

            <!-- 分步梳理步骤模块 -->
            <div v-if="item.type === 'stepList'" class="step-wrap">
              <div v-for="step in item.stepList" :key="step" class="step-item">
                <span class="check-mark">✓</span>
                <span class="step-txt">{{ step }}</span>
              </div>
            </div>

            <!-- 文书预览卡片（按钮横向不换行） -->
            <div v-if="item.type === 'docPreview'" class="preview-card">
              <div class="preview-title">{{ item.previewTitle }}</div>
              <div class="preview-content" v-html="item.content "></div>
              <div class="preview-btn-group">
                <!-- <button @click="handleInsertPreview(item.content)" class="btn-insert">插入</button> -->
                <button @click="handleCopyPreview(item.content)" class="btn-copy">复制</button>
                <button @click="handleDownloadWord(item.fileDownloadUrl, item.fileName)" class="btn-word">生成Word并打开</button>
              </div>
            </div>
          </div>
        </template>

        <!-- 用户消息：气泡左，头像最右侧 -->
        <template v-else>
          <div class="avatar user-avatar"><span class="avatar-text">我</span></div>
          <div class="message-content">
            <div class="bubble user-bubble">{{ item.content }}</div>
          </div>
        </template>
      </div>

      <!-- AI思考loading -->
      <div v-if="thinking || isStreaming" class="thinking-row">
        <div class="avatar ai-avatar"><span class="avatar-text">AI</span></div>
        <span></span><span></span><span></span>
        AI 正在思考...
      </div>
    </div>

    <!-- 底部输入区：上传与发送同行可见 -->
    <div class="input-footer">
      <div class="textarea-wrap">
        <a-textarea
          v-model:value="inputText"
          :rows="3"
          placeholder="请输入你的修改要求，例如：补充利息计算方式、调整诉讼请求等..."
          @keydown.enter.prevent="send"
        />
        <!-- 上传+发送同行，z-index提高防止遮挡 -->
        <div class="bottom-btn-row">
          <!-- <a-button class="up-btn" @click="uploadFile">上传文件</a-button> -->
          <div class="send-arrow" @click="send">
            <img src="@/assets/docImg/icon6.png" class="icons">
          </div>
        </div>
      </div>
      <div class="tip-desc">内容由 AI 生成，仅供参考，请注意甄别。</div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, watch } from "vue";
import { message } from 'ant-design-vue'
import { StartConversation, ContinueConversation } from '../../../api/case/case.js'
const emit = defineEmits(["apply", "export-word", "download-word", "quota-exceeded"]);

const props = defineProps({
  initialQuery: {
    type: String,
    default: ''
  },
  initialStructured: {
    type: Object,
    default: null
  }
})

const inputText = ref("");
const thinking = ref(false);
const messageBox = ref();
let msgId = 10;

// SSE 流状态
const isStreaming = ref(false)
// 对话 sessionId，首次启动接口返回后保存
let sessionId = ''

// 四种会话类型：text/file/stepList/docPreview
const messages = ref([
  {
    id: 1,
    role: "assistant",
    type: "text",
    content: "您好，我可以帮助您分析案件事实、生成法律文书。"
  }
]);

// 有初始数据时调用 Start 接口（首次挂载 + 后续更新）
watch(() => props.initialQuery, async (val) => {
  if (val) {
    await startConversation(val)
  }
}, { immediate: true })

// initialStructured 变化时也调用（表单提交场景）
watch(() => props.initialStructured, async (val) => {
  if (val && !props.initialQuery) {
    await startConversation(val)
  }
}, { immediate: true })

async function startConversation(query) {
  // 先插入用户消息
  const userContent = typeof query === 'string'
    ? query
    : `原告：${query.plaintiffInfo || ''}\n被告：${query.defendantInfo || ''}\n案件背景：${query.caseBackground || ''}\n诉讼请求：${query.claims || ''}`
  messages.value.push({
    id: msgId++,
    role: 'user',
    type: 'text',
    content: userContent
  })
  // 滚动到底部
  await nextTick()
  if (messageBox.value) messageBox.value.scrollTop = messageBox.value.scrollHeight

  thinking.value = true
  isStreaming.value = true
  // 展示"正在思考"后滚动到底部
  await nextTick()
  if (messageBox.value) messageBox.value.scrollTop = messageBox.value.scrollHeight

  const requestBody = props.initialStructured
    ? { inputType: 'structured', structured: props.initialStructured }
    : { inputType: 'natural', naturalLanguage: query }

  try {
    const response = await StartConversation(requestBody)
    // 检查 JSON 错误响应（如配额超限）
    const contentType = response.headers.get('content-type') || ''
    if (contentType.includes('json')) {
      const json = await response.json()
      // 外层 isSuccess=true，内层 data.code === 403
      if (json?.data?.code === 403) {
        const msg = json.data.msg || '今日起诉状生成使用次数已达上限，请明天再试'
        emit('quota-exceeded', msg)
        return
      }
    }
    await readSSEStream(response)
  } catch (err) {
    console.error('StartConversation 请求异常：', err)
    messages.value.push({
      id: msgId++,
      role: 'assistant',
      type: 'text',
      content: '请求异常，请检查网络后重试'
    })
  }

  thinking.value = false
  isStreaming.value = false
  nextTick().then(() => {
    if (messageBox.value) messageBox.value.scrollTop = messageBox.value.scrollHeight
  })
}

// HTML转义字符反转义
const unescapeHtml = (str) => {
  if (!str) return ''
  return str
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
}

// 解析 SSE 流数据，返回时 thinking/isStreaming 由调用方管理
async function readSSEStream(response) {
  if (!response.ok || !response.body) {
    messages.value.push({
      id: msgId++,
      role: 'assistant',
      type: 'text',
      content: '接口请求失败，请稍后重试'
    })
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''
  let streamRunning = true
  let currentTextContent = ''
  let textMsgAdded = false

  while (streamRunning) {
    const { done, value } = await reader.read()
    if (done) {
      streamRunning = false
      break
    }

    buffer += decoder.decode(value, { stream: true })

    while (buffer.includes('\n\n')) {
      const splitIndex = buffer.indexOf('\n\n')
      const fullMsg = buffer.slice(0, splitIndex).trim()
      buffer = buffer.slice(splitIndex + 2)

      if (!fullMsg) continue
      if (fullMsg === 'data:[DONE]') {
        streamRunning = false
        break
      }

      if (fullMsg.startsWith('data:')) {
        let jsonStr = fullMsg.slice(5).trim()
        if (!jsonStr) continue

        try {
          const parsed = JSON.parse(jsonStr)
          const type = parsed.type
          
          if (type === 'session' && parsed.data?.sessionId) {
            sessionId = parsed.data.sessionId
            window.__docSessionId = sessionId // 全局共享，供保存使用
          } else if (type === 'text' && parsed.data?.chunk) {
            currentTextContent += parsed.data.chunk

            if (!textMsgAdded) {
              messages.value.push({
                id: msgId++,
                role: 'assistant',
                type: 'text',
                content: unescapeHtml(currentTextContent)
              })
              textMsgAdded = true
            } else {
              const last = messages.value[messages.value.length - 1]
              if (last && last.role === 'assistant' && last.type === 'text') {
                last.content = currentTextContent
              }
            }

            thinking.value = false
            nextTick().then(() => {
              if (messageBox.value) messageBox.value.scrollTop = messageBox.value.scrollHeight
            })
          } else if (type === 'indictment' && parsed.data?.fullText) {
            // const pureHtml = unescapeHtml(indictmentData.fullText)
            // 文书生成完成——添加文书预览卡片
            const indictmentData = parsed.data
            messages.value.push({
              id: msgId++,
              role: 'assistant',
              type: 'docPreview',
              previewTitle: indictmentData.fileName?.replace('.docx', '') || '起诉状',
              content: unescapeHtml(indictmentData.fullText),
              fileDownloadUrl: indictmentData.fileDownloadUrl || '',
              fileName: indictmentData.fileName || ''
            })
            thinking.value = false
            nextTick().then(() => {
              if (messageBox.value) messageBox.value.scrollTop = messageBox.value.scrollHeight
            })
          } else if (type === 'done') {
            streamRunning = false
            break
          }
        } catch (e) {
          // json parse 失败，跳过
        }
      }
    }
  }
}

// 发送消息（后续对话，使用已保存的 sessionId）
const send = async () => {
  if (isStreaming.value) {
    message.info('请等待当前回复完成')
    return
  }
  if (!inputText.value.trim()) return;
  const text = inputText.value;
  messages.value.push({
    id: msgId++,
    role: "user",
    type: "text",
    content: text
  });
  inputText.value = "";
  await nextTick()
  if (messageBox.value) messageBox.value.scrollTop = messageBox.value.scrollHeight
  thinking.value = true;
  isStreaming.value = true
  await nextTick()
  if (messageBox.value) messageBox.value.scrollTop = messageBox.value.scrollHeight

  try {
    const response = await ContinueConversation(sessionId, { answer: text })
    await readSSEStream(response)
  } catch (err) {
    console.error('ContinueConversation 请求异常：', err)
    messages.value.push({
      id: msgId++,
      role: 'assistant',
      type: 'text',
      content: '请求异常，请检查网络后重试'
    })
  }

  thinking.value = false
  isStreaming.value = false
  nextTick().then(() => {
    if (messageBox.value) messageBox.value.scrollTop = messageBox.value.scrollHeight
  })
};

/**
 * 预览卡片【插入】传给父组件编辑器
 */
const handleInsertPreview = async (text) => {
  emit("apply", text);
};

/**
 * 预览卡片【复制】
 */
const handleCopyPreview = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    alert("复制成功");
  } catch (err) {
    alert("复制失败，请手动复制");
  }
};

/**
 * 预览卡片【下载并打开 Word】
 */
const handleDownloadWord = (url,name) => {
  if (!url) {
    alert('文件下载链接无效')
    return
  }
  emit('download-word', url,name)
};

// 上传文件
// const uploadFile = () => {
//   console.log("上传文件");
// };

// 对外暴露
defineExpose({
  handleInsertPreview,
  handleCopyPreview
});
</script>

<style scoped lang="less">
.ai-panel {
  height: 100%;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e4eaf5;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 顶部Header */
.ai-header {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  border-bottom: 1px solid #edf1f7;
  .ai-title {
    font-size: 16px;
    font-weight: 700;
    color: #182230;
  }
}

/* 消息容器 */
.message-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.message-container::-webkit-scrollbar {
  width: 4px;
}

.message-container::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 2px;
}

.message-container::-webkit-scrollbar-track {
  background: transparent;
}

.message-item {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  align-items: flex-start;
  /* AI消息正常顺序 */
  &.assistant {
    flex-direction: row;
  }
  /* 用户消息反转，头像在最右侧 */
  &.user {
    flex-direction: row-reverse;
  }
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
  line-height: 1;
  text-align: center;
  padding: 0;
}
.avatar-text {
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}
.ai-avatar {
  background: #1768ff;
  color: #fff;
}
.user-avatar {
  background: #1768ff;
  color: #fff;
}

.message-content {
  max-width: calc(100% - 80px);
  min-width: 0;
}

/* 基础气泡 */
.bubble {
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 22px;
  word-break: break-all;
}
.ai-bubble {
  background: #f5f8ff;
  color: #26354d;
}
.user-bubble {
  background: #f0f3fa;
  color: #182230;
}

/* 文件卡片 防止横向溢出 */
.file-card {
  margin-top: 8px;
  max-width: 100%;
  box-sizing: border-box;
  background: #f5f8ff;
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  .file-w-icon {
    width: 40px;
    height: 40px;
    background: #d6e4ff;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: bold;
    color: #1677ff;
    flex-shrink: 0;
  }
  .file-text-wrap {
    flex: 1;
    min-width: 0;
    .file-name {
      font-size: 14px;
      font-weight: 500;
      color: #1f2937;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .file-size {
      font-size: 12px;
      color: #6b7280;
    }
  }
  .file-done {
    color: #17A66A;
    font-weight: 500;
    flex-shrink: 0;
  }
  .download-box {
    width: 32px;
    height: 32px;
    border-radius: 4px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpath d='M12 5v14M19 12l-7 7-7-7'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    flex-shrink: 0;
    cursor: pointer;
  }
}

/* 分步梳理模块 */
.step-wrap {
  margin-top: 8px;
  max-width: 100%;
  box-sizing: border-box;
  background: #f5f8ff;
  border-radius: 10px;
  padding: 12px 16px;
  .step-item {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
    align-items: flex-start;
    .check-mark {
      color: #1768FF;
      font-weight: bold;
      flex-shrink: 0;
    }
    .step-txt {
      font-size: 14px;
      color: #374151;
      line-height: 22px;
      word-break: break-all;
    }
  }
}

/* 文书预览卡片：按钮强制横向不换行 */
.preview-card {
  margin-top: 8px;
  max-width: 100%;
  box-sizing: border-box;
  background: #f5f8ff;
  border-radius: 10px;
  padding: 14px;
  .preview-title {
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 10px;
  }
  .preview-content {
    font-size: 13px;
    line-height: 20px;
    color: #333;
    max-height: 220px;
    overflow-y: auto;
    margin-bottom: 12px;
    white-space: pre-line;
  }
  .preview-btn-group {
    display: flex;
    gap: 8px;
    /* 核心：禁止按钮自动换行，全部横向一行 */
    flex-wrap: nowrap;
    overflow-x: hidden;
    button {
      padding: 4px 10px;
      border: none;
      border-radius: 4px;
      font-size: 12px;
      cursor: pointer;
      /* 按钮文字不换行 */
      white-space: nowrap;
    }
    .btn-insert {
      font-weight: 600;
      font-size: 12px;
      color: #8046DA;
      background: #F0E6FF;
      border-radius: 11px;
      border: 1px solid #E9DCFF;
    }
    .btn-copy {
      background: #FFFFFF;
    border-radius: 11px 11px 11px 11px;
    border: 1px solid #DCE5F1;
    font-weight: 600;
    font-size: 12px;
    color: #263957;
    }
    .btn-word {
      font-weight: 600;
      font-size: 12px;
      color: #263957;
      background: #FFFFFF;
      border-radius: 11px 11px 11px 11px;
      border: 1px solid #DCE5F1;
    }
  }
}

/* AI加载动画 */
.thinking-row {
  margin-left: 0;
  font-size: 13px;
  color: #98a2b3;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  span {
    width: 6px;
    height: 6px;
    background: #1768ff;
    border-radius: 50%;
    animation: loading 1.2s infinite;
  }
  span:nth-child(2) { animation-delay: 0.2s; }
  span:nth-child(3) { animation-delay: 0.4s; }
}
@keyframes loading {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
}

/* 底部输入区域：修复上传按钮看不见 */
.input-footer {
  border-top: 1px solid #edf1f7;
  padding: 14px 16px 16px;
  box-sizing: border-box;
}
.textarea-wrap {
  position: relative;
  :deep(.ant-input) {
    border-radius: 10px;
    /* 底部预留按钮空间 */
    padding-bottom: 50px;
  }
  /* 上传+发送同行，z-index加高防止遮挡 */
  .bottom-btn-row {
    position: absolute;
    left: 8px;
    right: 8px;
    bottom: 8px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    z-index: 10;
    /* 底部半透明白色遮罩隔绝输入文字 */
    background: linear-gradient(to top, #ffffff 75%, transparent);
    padding: 4px 0;
  }
  .send-arrow {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #1677ff;
    // background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='white' viewBox='0 0 16 16'%3E%3Cpath d='M1.59 1.207a.5.5 0 0 1 .63.63l10.8 4.5a.5.5 0 0 1 0 .907l-10.8 4.5a.5.5 0 0 1-.63-.63L3.744 8 1.59 1.207Z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
.up-btn{
  background: #FFFFFF;
  box-shadow: 0px -10px 24px 0px rgba(35,66,112,0.06);
  border-radius: 9px 9px 9px 9px;
  border: 1px solid #DFE6F1;
  font-weight: 400;
  font-size: 13px;
  color: #40516A;
  line-height: 18px;
  text-shadow: 0px -10px 24px rgba(35,66,112,0.06);
  text-align: center;
}
.icons{
  width: 20px;
  height: 20px;
}
.tip-desc {
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
  margin-top: 10px;
}
</style>